<template>
  <v-container fluid justify-start>
    <loading
      slot="activator"
      :active.sync="isDeleting"
      :can-cancel="false"
      loader="dots"
      :color="appColor_Progress"
    />

    <!-- Data Table -->
    <v-data-table
      :headers="headers"
      :items="filteredDomains"
      :loading="isLoading"
      :search="search"
      :custom-filter="customFilter"
      :no-data-text="noDataText"
      item-key="id"
      :loading-text="getExpression('Alert', 'LoadingItems')"
      :footer-props="{
        'items-per-page-text': getExpression('Label', 'RowsPerPage')
      }"
      class="elevation-2"
      >
      <v-progress-linear slot="progress" :color="appColor_Progress" indeterminate />

      <template v-slot:top>

        <!-- Header Toolbar -->
        <v-toolbar dense flat dark :color="toolbarColor">
          
          <app-back-button v-bind:color="toolbarColorDark" />
          
          <v-toolbar-title :class="titleClass">Domains</v-toolbar-title>

          <v-spacer />

          <!-- Edit Dialog -->
          <v-toolbar-items>
            <v-dialog 
              v-model="editDialog" 
              :max-width="dialogWidth" 
              :persistent="true">

              <template v-slot:activator="{ on }">
                <v-btn v-if="isSmallDevice"
                  small
                  slot="activator"
                  :disabled="isLoading"
                  :color="toolbarColorDark"
                  class="mb-1 mt-0 mr-0 elevation-0"
                  v-on="on"
                  >New
                </v-btn>
                <v-btn v-else
                  slot="activator"
                  :disabled="isLoading"
                  :color="toolbarColorDark"
                  class="mb-1 mt-0 mr-0 elevation-0"
                  v-on="on"
                  >Create Domain
                </v-btn>
              </template>

              <v-card>
                <v-toolbar dense dark flat :color="toolbarColor">
                  <v-toolbar-title>
                    <v-icon class="mr-2">edit</v-icon>
                      {{ formTitle }}
                  </v-toolbar-title>
                </v-toolbar>

                <v-card-text class="pb-0 px-2">
                  <v-container grid-list-md>
                    <form>

                      <!-- Organization -->
                      <v-row no-gutters>
                        <!-- Create a new domain -->
                        <v-col cols="12" sm="12" >
                          <organization-combobox
                            v-model="editedDomain.organizationId"
                            :options="organizationsForSelection"
                            :label="getExpression('Label', 'Organization')"
                            v-validate="'required'"
                            :error="errors.collect('organization')"
                            data-vv-name="organization"
                          />
                        </v-col>
                      </v-row>

                      <!-- Domain Name -->
                      <v-row no-gutters>
                        <v-col cols="12" sm="12">
                          <v-text-field
                            v-model="editedDomain.name"
                            :label="getExpression('Label', 'Name')"
                            hide-details="auto"
                            class="mb-4"
                            ref="name"
                            :error-messages="errors.first('name')"
                            v-validate="'required|max:200'"
                            data-vv-name="name"
                          />
                        </v-col>
                      </v-row>

                      <!-- Domain Description -->
                      <v-row no-gutters>
                        <v-col cols="12" sm="12">
                          <v-text-field
                            v-model="editedDomain.description"
                            class="mb-4"
                            :label="getExpression('Label', 'Description')"
                            hide-details="auto"
                            ref="description"
                          />
                        </v-col>
                      </v-row>

                      <!-- Assigned Facilities -->
                      <v-row no-gutters>
                        <v-col cols="12" sm="12">
                          <facility-selector
                            v-if="showFacilitiesComponent"
                            v-model="editedDomain.facilityIds"
                            :options="selectorFacilities"
                            :label="getExpression('Label', 'AssignedFacilities')"
                            :multiple="true"
                            :close="false"
                            :chipColor="appColor_Dark"
                          />
                        </v-col>
                      </v-row>
                    </form>
                  </v-container>
                </v-card-text>
                <v-card-actions class="pb-3">

                  <v-spacer />
                  
                  <v-btn @click="closeDialog" :loading="isSaving" class="rounded-0" large>{{ getExpression('Button', 'Cancel') }}</v-btn>
                  <v-btn :color="appColor_Dark" dark @click="save" :loading="isSaving" class="rounded-0 ml-4" large>{{ getExpression('Button', 'Save') }}</v-btn>
                
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-toolbar-items>
        </v-toolbar>

        <!-- Filters -->
          <v-row no-gutters class="mx-3 mt-1">
            <!-- Organization filter -->
            <v-col cols="12" md="3">
              <organization-combobox
                v-model="organizationId"
                :options="organizationsForFilter"
                :label="getExpression('Label', 'Organization')"
                :disabled="isLoading"
              />
            </v-col>

            <!-- Search -->
            <v-col cols="12" md="9">
              <v-text-field
                v-model="search"
                append-icon="search"
                :label="getExpression('Label', 'Search')"
                single-line
                hide-details
                :disabled="isLoading"
                clearable
                class="ml-3"
              />
            </v-col>
          </v-row>
      </template>

      <template v-slot:item.name="{ item }">
        <span class="text-left" style="white-space: nowrap">
          <span @click="edit(item)">
            <span :style="appStyle_HyperLink">{{ item.name }}</span>
            <v-icon small style="cursor: pointer" class="ml-2">edit</v-icon>
          </span>
        </span>
      </template>

      <template v-slot:item.organizationName="{ item }">
        <span class="text-left">{{ item.organizationName }}</span>
      </template>

      <template v-slot:item.assignedFacilities="{ item }">
        <span class="text-left">{{ item.assignedFacilities }}</span>
      </template>

      <template v-slot:item.users="{ item }">
        <span class="text-left">{{ item.users }}</span>
      </template>

      <template v-slot:item.kioskCount="{ item }">
        <div class="text-center" v-html="getKioskCountHtml(item)"></div>
      </template>

      <template v-slot:item.action="{ item }">
          <v-icon @click="remove(item)">delete</v-icon>
      </template>
    </v-data-table>
  </v-container>
</template>

<script>
import appService from '@/app.service'
import { mapGetters } from 'vuex'
import FacilitySelector from '@/components/FacilitySelector'
import OrganizationCombobox from '@/components/OrganizationCombobox.vue'
import AppBackButton from '@/components/App/AppBackButton'
import { AppColor, AppStyle, LanguageId } from '@/config.js'

export default {
  name: 'DomainManager',
  components: {
    FacilitySelector,
    OrganizationCombobox,
    AppBackButton
  },
  $_veeValidate: {
    validator: 'new'
  },
  data() {
    return {
      // colors
      appColor_Medium: AppColor.Medium,
      appColor_Dark: AppColor.Dark,
      appColor_Progress: AppColor.Progress,
      appColor_Abby: AppColor.KioskTypeAbby,
      appColor_Genie: AppColor.KioskTypeGenie,

      // styles
      appStyle_HyperLink: AppStyle.HyperLink,

      isLoading: true,
      isSaving: false,
      isDeleting: false,
      search: '',
      editDialog: false,
      domains: [],
      organizations: [],
      organizationsForSelection: [],
      organizationsForFilter: [],
      organizationId: null,
      facilities: [],
      assignedFacilityIds: [],
      availableFacilityIds: [],

      editedIndex: -1,
      editedDomain: {
        id: -1,
        name: null,
        organizationId: null,
        facilityIds: null,
        users: null,
        genieCount: null,
        abbyCount: null,
        createDate: null
      },
      defaultDomain: {
        id: -1,
        name: null,
        organizationId: null,
        facilityIds: null,
        users: null,
        genieCount: null,
        abbyCount: null,
        createDate: null
      },
      restoreDomain: {
        id: -1,
        name: null,
        organizationId: null,
        facilityIds: null,
        users: null,
        genieCount: null,
        abbyCount: null,
        createDate: null
      },
      headers: [
        {
          text: 'Domain',
          value: 'name',
          align: 'left',
          width: '15%',
          class: 'pr-0'
        },
        {
          text: 'Organization',
          value: 'organizationName',
          align: 'left',
          width: '20%',
          class: 'pr-0'
        },
        {
          text: 'Description',
          value: 'description',
          align: 'left',
          width: '20%',
          class: 'pr-0'
        },
        {
          text: 'Facilities',
          value: 'assignedFacilities',
          align: 'left',
          width: '25%',
          class: 'pr-0'
        },
        {
          text: 'Domain Managers',
          value: 'users',
          align: 'left',
          width: '10%',
          class: 'pr-0'
        },
        {
          text: 'Kiosks',
          value: 'kioskCount',
          align: 'center',
          width: '10%',
          class: 'pr-0'
        },
        {
          value: 'action',
          sortable: false,
          align: 'center',
          width: '5%'
        }
      ],
    }
  },
  computed: {
    ...mapGetters([
      'profile',
      'locale']),
    noDataText () {
      if (this.isLoading) return this.getExpression('Alert', 'OneMoment')

      if (this.filteredDomains.length === 0) {
        return this.getExpression('Alert', 'NoDomains')
      }
      return ''
    },
    formTitle () {
      return this.editedIndex < 0 ? 'New Domain' : 'Edit Domain'
    },
    dictionary () { 
      return {
        attributes: {
          name: 'Name',
          organization: 'Organization'
        },
        custom: {
          name: {
            required: () => this.getExpression('Validation', 'NameRequired'),
            max: this.getExpression('Validation', 'NameMaxChars').replace('{0}', '200'),
            alpha_spaces: this.getExpression('Validation', 'NameAlphaSpaces')
          },
          organization: {
            required: () => this.getExpression('Validation', 'OrganizationRequired'),
          },
          facility: {
            required: () => this.getExpression('Validation', 'FacilityRequired')
          }
        }
      }
    },
    toolbarColor () {
      return this.appColor_Medium
    },
    toolbarColorDark () {
      return this.appColor_Dark
    },
    showFacilitiesComponent () {
      return this.editedDomain.organizationId > -1
    },
    filteredDomains() {
      const self = this
      if (this.domains.length === 0) return []

      const organizationId = this.organizationId
      return self.domains.filter(o => {
        return (o.organizationId === organizationId || organizationId === 0)
      })
    },
    selectorFacilities () {
      const self = this
      const editedDomain = self.editedDomain;
      const initialFacilityIds = self.restoreDomain.facilityIds
      const facilitiesReturn = [];

      if (editedDomain.id !== -1 && typeof editedDomain.id !== 'undefined') {
        initialFacilityIds.forEach(id => {
          const facility = self.facilities.find(f => f.id === id)
          
          if (facility !== null) {
            facilitiesReturn.push(
              { 
                id: facility.id, 
                name: facility.name, 
                geographicalDescription: facility.geographicalDescription 
              });
          }
        });
      }
      self.facilities
        .filter(({ id }) => self.availableFacilityIds.includes(id))
        .forEach(({ id, name, geographicalDescription }) => {
          facilitiesReturn.push({ id, name, geographicalDescription });
        });
      return facilitiesReturn;
    },
    // styles
    dialogWidth () {
      return this.profile.languageId === LanguageId.English
        || this.editedIndex < 0
        ? '380px'
        : '480px'
    },
    titleClass () {
      return this.isSmallDevice
        ? 'body-2'
        : ''
    }
  },
  methods: {
    loadData () {
      const self = this
      self.isLoading = true

      appService.getDomainsForManager().then(data => {
        if (data.errorSeverity !== null) {
          self.handleError(data)
        } else {
          // store lists
          self.facilities = data.facilities
          self.organizations = data.organizations
          
          // need a separate selection list for validation
          const { organizations } = data
          self.organizationsForSelection = organizations || []
          self.organizationsForFilter = [{ id: 0, name: '(All)', createDate: null }, ...self.organizationsForSelection]
          self.organizationId = 0

          self.domains = data.domains.map(d => { return {
            id: d.id,
            name: d.name,
            description: d.description,
            organizationId: d.organizationId,
            organizationName: this.getOrganizationName(d.organizationId),
            facilityIds: d.facilityIds,
            assignedFacilities: this.getAssignedFacilities(d.facilityIds),
            users: d.users,
            genieCount: d.genieCount,
            abbyCount: d.abbyCount,
            createDate: d.createDate
          }})

          self.updateAvailableFacilities()
          self.isLoading = false
        }
      })
    },

    edit (item) {
      const self = this
      self.editedIndex = self.domains.indexOf(item)
      self.editedDomain = Object.assign({}, item)
      self.restoreDomain = Object.assign({}, item)
      self.editDialog = true
    },

    save () {
      const self = this
      self.$validator.resume()
      self.$validator.validateAll().then(valid => {
        if (valid) {
          self.isSaving = true
          const isEditMode = self.editedIndex > -1
          
          // Scrub html tags from text inputs
          self.editedDomain.name = self.editedDomain.name.toString().trim().replace(/</g, "&lt;").replace(/>/g, "&gt;")
          self.editedDomain.description = self.editedDomain.description != null 
            ? self.editedDomain.description.toString().trim().replace(/</g, "&lt;").replace(/>/g, "&gt;") 
            : null

          if (isEditMode) {
            self.editedDomain.facilityIds = self.editedDomain.facilityIds || []
            var editedDomain = self.domains[self.editedIndex]
            Object.assign(editedDomain, self.editedDomain)
            const editModel = self.getModel(self.editedDomain, isEditMode)

            appService.updateDomain(editedDomain.id, editModel).then(data => {
              if (data.errorSeverity !== null) {
                self.handleError(data)
                Object.assign(self.domains[self.editedIndex], self.restoreDomain)
              } else {
                const updatedDomainIndex = self.domains.findIndex(d => d.id === data.domain.id)
                if (updatedDomainIndex > -1) {
                  self.domains[updatedDomainIndex].assignedFacilities = self.getAssignedFacilities(data.domain.facilityIds)
                  self.domains[updatedDomainIndex].organizationName = self.getOrganizationName(data.domain.organizationId)
                  self.domains[updatedDomainIndex].genieCount = data.domain.genieCount
                  self.domains[updatedDomainIndex].abbyCount = data.domain.abbyCount
                }
                self.closeDialog()
              }
              self.updateAvailableFacilities()
              self.isSaving = false
              Object.assign(self.editedDomain, self.defaultDomain)
              self.$validator.pause()
            }).catch(e => { self.handleCriticalError(e) })
          } else {
            const addModel = self.getModel(self.editedDomain, isEditMode)

            appService.addDomain(addModel).then(data => {
              if (data.errorSeverity !== null) {
                self.handleError(data)
              } else {
                self.closeDialog()
                var newDomain = Object.assign({}, data.domain)
                newDomain.facilityIds = data.domain.facilityIds ?? []
                newDomain.assignedFacilities = self.getAssignedFacilities(data.domain.facilityIds)
                newDomain.organizationName = self.getOrganizationName(data.domain.organizationId)
                self.domains.unshift(newDomain)
              }
              self.updateAvailableFacilities()
              self.isSaving = false
              self.$validator.pause()
            }).catch(e => { self.handleCriticalError(e) })
          }
        }
      })
    },

    remove (item) {
      const self = this
      const index = self.filteredDomains.indexOf(item)

      self.$confirm.show({
        title: 'Delete Domain?',
        text: self.getDeleteDomainText(item),
        onConfirm: () => {

          self.isDeleting = true
          self.validateDelete(item).then(result => {
            if (result.errorSeverity !== null) {
              self.isDeleting = false
              self.handleError(result)
            } else {
              appService.deleteDomain(item.id).then((data) => {
                if (data.errorSeverity > 0) {
                  self.handleError(data)
                } else {
                  self.domains.splice(index, 1)
                }
                self.updateAvailableFacilities()
                self.isDeleting = false
              }).catch(e => { self.handleCriticalError(e) })
            }
          })
        }
      })
    },

    validateDelete (item) {
      return new Promise((resolve) => {
        const self = this

        self.validateDomainsForOrg(item.organizationId, item.name, false).then(data => {
          if (data.errorSeverity !== null) {
            resolve(data)
          } else {
            self.validateDomainManagers(item.id, item.name).then(data => {
              resolve(data)
            })
          }
        })
      })
    },

    validateDomainManagers(id, name) {
      return new Promise(resolve => {
        appService.getDomainManagers(id).then((data) => {
          if (data.errorSeverity !== null) {
            resolve(data)
          } else {
            if (data.users.length === 0) {
              resolve({ errorSeverity: null })
            }

            const users = data.users
            var list = ''

            users.forEach((u) => {
              list += `${u.name}<br/>`
            })

            // pluralize
            const result =
              users.length === 1
                ? `<b>${name}</b> still has a <i>Domain Manager</i> assigned to it:<br/><br/>` +
                  list +
                  `<br/>You must unassign <b>${name}</b> from this user before this domain can be deleted.`
                : `<b>${name}</b> still has ${users.length} <i>Domain Managers</i> associated to it:<br/><br/>` +
                  list +
                  `<br/>You must unassign <b>${name}</b> from all of the above users before attempting to delete this domain.`

            resolve({ 
              errorMessage: result,
              errorSeverity: 0
            })
          }
        }).catch(e => { self.handleCriticalError(e) })
      })
    },

    getDeleteDomainText (domain) {
      const name = domain.name

      if (domain.facilityIds.length === 0 || domain.facilityIds === null) return `<b>${name}</b>`

      const facilities = this.facilities.filter(f => domain.facilityIds.includes(f.id))
      var list = ''

      facilities.forEach((f) => {
        list += `<li>${f.name}</li>`
      })
      
      // pluralize
      var result = `<b>${name}</b><br/><br/>`
      result += facilities.length === 1
          ? `The following facility will be unassigned from this domain:<ul>`
          : `The following ${facilities.length} facilities will be unassigned from this domain:<ul>`
      result += list
      result += '</ul>'
      return result
    },

    validateDomainsForOrg(orgId, domainName) {
      return new Promise(resolve => {
        // check if it's the last domain in the organization
        appService.getOrganizationDomains(orgId).then((data) => {
          if (data.errorSeverity > 0) {
            resolve(data)
          } else {
            if (data.domains.length > 1) {
              resolve({ errorSeverity: null })
            }

            const result = `Could not delete <b>${domainName}</b> because it's the last domain in the organization.`
            resolve({ 
              errorMessage: result,
              errorSeverity: 0
            })
          }
        }).catch(e => { self.handleCriticalError(e) })
      })
    },

    updateAvailableFacilities () {
      const self = this
      self.assignedFacilityIds = self.domains.flatMap(d => d.facilityIds) || [];
      self.availableFacilityIds = self.facilities
        .filter(f => !self.assignedFacilityIds.includes(f.id))
        .map(f => f.id);
    },

    getModel (d, isEditMode) {
      if (isEditMode) {
        return {
          name: d.name.toString().trim(),
          description: d.description != null ? d.description.toString().trim().replace(/</g, "&lt;").replace(/>/g, "&gt;") : null,
          organizationId: d.organizationId,
          facilityIds: d.facilityIds,
          createDate: d.createDate
        }
      } else {
        return {
          name: d.name.toString().trim(),
          description: d.description != null ? d.description.toString().trim().replace(/</g, "&lt;").replace(/>/g, "&gt;") : null,
          organizationId: d.organizationId,
          facilityIds: d.facilityIds
        }
      }
    },

    customFilter (value, search, item) {
      const description = item.description != null ? item.description : ''
      const users = item.users != null ? item.users : ''

      return (
        (value != null &&
          search != null &&
          item.name
            .toString()
            .toLowerCase()
            .indexOf(search.toLowerCase()) !== -1) ||
          item.organizationName
            .toString()
            .toLowerCase()
            .indexOf(search.toLowerCase()) !== -1 ||
          item.assignedFacilities
            .toString()
            .toLowerCase()
            .indexOf(search.toLowerCase()) !== -1 ||
          description
            .toString()
            .toLowerCase()
            .indexOf(search.toLowerCase()) !== -1 ||
          users
            .toString()
            .toLowerCase()
            .indexOf(search.toLowerCase()) !== -1 
      )
    },

    getAssignedFacilities (ids) {
      if (this.isNullOrUndefined(ids)) return
      var facilities = this.facilities.filter(f => ids.includes(f.id))
      var assignedFacilities = ''
      
      facilities.forEach(f => {
        assignedFacilities.length === 0 
          ? assignedFacilities = f.name
          : assignedFacilities += ', ' + f.name
      })
      return assignedFacilities
    }, 

    getOrganizationName (id) {
      const organizations = this.organizations.filter((o) => {
        return o.id === id
      })
      return organizations.length > 0 ? organizations[0].name : null
    },

    closeDialog () {
      this.editDialog = false
      setTimeout(() => {
        this.editedIndex = -1
        this.resetEditedDomain()
      }, 300)
    },

    resetEditedDomain () {
      this.editedDomain = Object.assign({}, this.defaultUser)
      this.editedIndex = -1
      this.$validator.reset()
    },

    getKioskCountHtml(item) {
      const genieCount = '<span style="color:' + this.appColor_Genie + '">' + item.genieCount + '</span>'
      const abbyCount = '<span style="color:' + this.appColor_Abby + '">' + item.abbyCount + '</span>'
      return abbyCount + ' | ' + genieCount
    },

    handleError (result) {
      this.$error.show({
        title: result.errorSeverity === 0
          ? this.getExpression('FormTitle', 'ValidationFailed')
          : this.getExpression('FormTitle', 'InternalError'),
        text: result.errorMessage,
        severity: result.errorSeverity
      })
    }
  },
  created() {
    this.loadData()
  },
  beforeDestroy() {
    this.$validator.pause()
  },
  mounted() {
    this.$validator.localize('en', this.dictionary)
    this.$validator.reset()
  },
  watch: {
    editDialog (val) {
      if (val) {
        const self = this
        self.$validator.reset()
        self.$validator.pause()
        // if (self.editedIndex < 0) self.resetEditedDomain()
        setTimeout(() => {
          self.$refs.name.focus()
        }, 300)
      } else {
        this.closeDialog()
      }
    }
  }
}
</script>

<style>
.truncate {
  max-width: 1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
